import { makeVar, useReactiveVar } from '@apollo/client';
import { useEventThemes } from '@components/app/useEventThemes';
import { BackgroundThemeProvider } from '@components/blocks/BlockThemeProvider';
import { iconPacks } from '@components/shared/Icon';
import type { BlocksParameters } from '@cvent/blocks/utils/types';
import { FormulaPreference } from '@cvent/blocks/utils/types';
import { ThemeProvider as CarinaThemeProviderV0 } from '@cvent/carina-theme-provider';
import { DEFAULT_ICON_PACK, formatPath, IconProvider } from '@cvent/carina-v2/components/IconProvider';
import { IconSettings, ThemeFormulaType, ThemeFragment } from '@graphql/gql/graphql';
import useIconFetch from '@hooks/useIconFetch';
import getReassignedIconsLookup, { IconSettingsWithLookup } from '@tools/getReassignedIconsLookup';
import React, { useCallback, useState } from 'react';

// Path to icons stored under /public/images/icons
const localIconPath = '/hub/images/icons/{pack}/{name}.svg#{pack}-{name}';

export function ThemeProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [defaultTheme] = useEventThemes();

  const { icons } = useThemeValues();

  const [iconPath, setIconPath] = useState(localIconPath);
  const [iconPack, setIconPack] = useState(icons?.pack && iconPacks[icons.pack] ? icons.pack : DEFAULT_ICON_PACK);

  const formatIconPath = useCallback(
    ({ path, pack, name }: { path?: string; pack?: string; name: string }): string =>
      formatPath({
        path: path ?? iconPath,
        pack: pack ?? iconPack,
        name
      }),
    [iconPath, iconPack]
  );

  return (
    <BackgroundThemeProvider>
      <IconProvider
        formatPath={formatIconPath}
        pack={iconPack}
        path={iconPath}
        setPack={setIconPack}
        setPath={setIconPath}
        useFetch={useIconFetch}
      >
        <CarinaThemeProviderV0 theme={defaultTheme}>{children}</CarinaThemeProviderV0>
      </IconProvider>
    </BackgroundThemeProvider>
  );
}

export default ThemeProvider;

export type ThemeValues = BlocksParameters & {
  hideIllustrations?: boolean;
  icons?: IconSettingsWithLookup;
};

export interface ThemeValuesInput extends Omit<ThemeValues, 'icons' | 'preferFormula'> {
  preferFormula: ThemeFormulaType;
  icons?: IconSettings;
}

export const themeValues = makeVar<ThemeValues>({
  primary: '#182261',
  secondary: '#006AE1',
  background: '#F4F4F4',
  mood: '#FFFFFF',
  safeMode: true,
  preferFormula: null,
  hideIllustrations: false,
  headingFont: null,
  bodyFont: null,
  icons: {
    pack: 'carina',
    reassignedIcons: [],
    reassignedIconsLookup: null
  }
});

export const useThemeValues = () => useReactiveVar(themeValues);

function mapPreferFormula(value: ThemeFormulaType): FormulaPreference {
  if (value === ThemeFormulaType.Dark.toString()) {
    return 'dark';
  }
  if (value === ThemeFormulaType.Light.toString()) {
    return 'light';
  }
  return null;
}

export function setThemeValues(values: ThemeValuesInput) {
  themeValues({
    ...values,
    preferFormula: mapPreferFormula(values.preferFormula),
    icons: {
      ...values?.icons,
      reassignedIconsLookup: getReassignedIconsLookup(values?.icons)
    }
  });
}

export function setThemeFromEventInfoTheme(values: ThemeFragment) {
  const { primary, background, secondary, mood, safeMode, preferFormula, illustrations, headingFont, bodyFont, icons } =
    values;
  themeValues({
    primary,
    secondary,
    background,
    mood,
    safeMode,
    preferFormula: mapPreferFormula(preferFormula),
    hideIllustrations: illustrations?.hideAll,
    headingFont,
    bodyFont,
    icons: {
      ...icons,
      reassignedIconsLookup: getReassignedIconsLookup(icons)
    }
  });
}
